import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useGetUserEntryQuery } from "services/auth/authService";
import { types, categories, SchemaRequired } from "types/carTypes";
import {
	scrollToTop,
	loadExistingData,
	setCategory,
	createFormData,
} from "helpers/utils";
import PSPage from "components/globals/ui/layout/Page";
import { EntryLoading, EntryError } from "components/globals/EntryStates";
import {
	PostTypes,
	PostCategories,
	PSInputBase,
	PSLightSwitch,
} from "components/globals/ui/input/PSInputs";
import { FormStage, ProgressBarCars } from "components/globals/forms/ORSFormLayout";
import PSMakeAndModel from "components/globals/ui/input/PSMakeAndModel";
import { useDispatch } from "react-redux";
import { createEntry, updateEntry } from "store/authSlice";
import { ErrorPane } from "components/modals/Panes";
import { useNavigate } from "react-router-dom";


/*
CREATE CAR
postData: the data assembled that will be sent to the backend
existingEntryData: the post being edited, set to postData with effect
*/

export default function CreateCarRequired({ edit = false }) {
	const { id } = useParams();
	const [loading, setLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState(null);
	const [postData, updatePostData] = useState(SchemaRequired);
	const userToken = useSelector((state) => state.auth.userToken);
	const dispatch = useDispatch();
	const navigate = useNavigate();


	const {
		data: existingEntryData,
		loading: entryLoading,
		error: entryError,
	} = useGetUserEntryQuery(`car/${id}`);

	// load existing data if editing
	useEffect(() => {
		if (edit && existingEntryData) {
			loadExistingData(updatePostData, SchemaRequired, existingEntryData);
		}
		setLoading(false);
	}, [existingEntryData]);


	useEffect(() => {
		if (!edit) {
			setNewPostDataProperty("type", types[0]?.key);
			setCategory(setNewPostDataProperty, categories, types[0]?.key);
		}
	}, []);

	// always default to the first category in the list when
	// updating the post type
	useEffect(() => {
		if (postData.type) {
			setCategory(setNewPostDataProperty, categories, postData.type);
		}
	}, [postData.type]);

	// coming from input onChange (text and select, type, category)
	const onInputChange = (context, val) => {
		setNewPostDataProperty(context, val);
	};

	const setNewPostDataProperty = (context, val) => {
		updatePostData((prevData) => ({
			...prevData,
			[context]: val,
		}));
	};

	// SUBMIT
	const submitForm = async () => {
		if (!userToken || (edit && !id)) return;

		if (!postData.title || postData.title.length <= 0) {
			alert("title required");
			return;
		}

		if (!postData.year || postData.year.length <= 0) {
			alert("vehicle year required");
			return;
		}
		
		if (!postData.make || postData.make.length <= 0) {
			alert("vehicle make required");
			return;
		}

		if (!postData.model || postData.model.length <= 0) {
			alert("vehicle model required");
			return;
		}

		// Set initial state for loading, messages, etc.
		setLoading(true);
		setErrorMessage(null);

		// create the form data for processing
		let fd = createFormData(postData, SchemaRequired);

		if (edit) {
			fd.append("internal_id", id);
		}

		try {
			const action = edit
				? updateEntry({ data: fd, entry_type: "garagecar" })
				: createEntry({ data: fd, entry_type: "garagecar" });
			const res = await dispatch(action).unwrap();
			navigate(`/edit/car/optional/${res._id}`);

		} catch (err) {
			setErrorMessage(err.message || "Failed. Please try again.");
		} finally {
			setLoading(false);
			scrollToTop();
		}
	};

	const clearErrorMessage = () => {
		setErrorMessage(null);
	};

	const onMakeAndModelChange = ({ make, model }) => {
		setNewPostDataProperty('make', make);
		setNewPostDataProperty('model', model);
	};

	if (entryLoading) {
		return <EntryLoading />;
	}
	if (entryError) {
		return <EntryError msg="Failed to load." />;
	}


	return (
		<PSPage>

			<ErrorPane
				visible={errorMessage}
				message={errorMessage}
				onClose={clearErrorMessage}
			/>

			<div className="relative">

				<ProgressBarCars stage={1} />

				<div className="mt-[60px]">

					{!loading && (
						<FormStage 
							heading={edit ? `Update garage car: ${postData?.title}` : "Create a new garage car"}
							subheading="Required Info" 
							onSave={submitForm}
							stage={1}
							stages={5}
						>
							<PostTypes
								postType={postData?.type}
								data={types}
								onInputChange={onInputChange}
							/>

							<PostCategories
								postType={postData?.type}
								postCategory={postData?.category}
								data={categories}
								onInputChange={onInputChange}
							/>

							<PSLightSwitch
								name="private"
								label="Mark Private?"
								value={postData["private"] || false}
								onChange={onInputChange}
							/>

							<PSInputBase
								name="title"
								label="Title"
								type="text"
								context="title"
								required
								value={postData.title || ""}
								onChange={onInputChange}
							/>

							<PSInputBase
								name="year"
								label="Year"
								type="year"
								context="year"
								value={postData.year || ""}
								onChange={onInputChange}
							/>

							<PSMakeAndModel
								initialMake={postData.make || ""}
								initialModel={postData.model || ""}
								onChange={onMakeAndModelChange}
							/>
						</FormStage>
					)}
				</div>
			</div>
		</PSPage>
	);
}
